import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BlockHeading } from 'components/BlockHeading';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LinkIcon } from 'i/icons/link.svg';
import { USERS_API } from 'api';
import { AuthContext } from 'context/auth';
import { PROMISE_STATES, USER_ROLES } from 'utils/constant';
import { Spinner } from 'components/Spinner';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import CompanyUsersBlock from './CompanyUsersBlock';

const CompanyUsers = ({ adminCompanyData }) => {
	const { t } = useTranslation('accountPage');
	const { currentUserRole, currentUserAdditionalData } = useContext(AuthContext);
	const [companyUsers, setCompanyUsers] = useState(null);
	const [loadingStatus, setLoadingStatus] = useState(PROMISE_STATES.default);
	const [loadingErrorMessage, setLoadingErrorMessage] = useState(null);
	const adminMode = !!(currentUserRole === USER_ROLES.admin && adminCompanyData);
	const companyId = useRef(
		(adminMode && adminCompanyData.id) || currentUserAdditionalData?.companyId
	);
	const navigate = useNavigate();

	const getAllCompanyUsers = useCallback(async () => {
		setLoadingStatus(PROMISE_STATES.pending);

		try {
			const companyUsersRes = await USERS_API.getUsersFromCompany(companyId.current);

			if (companyUsersRes) {
				setCompanyUsers(companyUsersRes);
				setLoadingStatus(PROMISE_STATES.fulfilled);
			}
		} catch (error) {
			setLoadingStatus(PROMISE_STATES.rejected);
			setLoadingErrorMessage(error.message);
		}
	}, []);

	const createInviteLinkToCompany = () => {
		if (companyId.current) {
			const currentHost = window.location.host;
			const inviteLink = `${currentHost}/register/${companyId.current}`;

			navigator.clipboard.writeText(inviteLink);

			toast.success(t('notifications:invitationLinkHasBeenCopied'), {
				autoClose: 2000,
				hideProgressBar: true,
				closeOnClick: false,
				closeButton: false,
			});
		}
	};

	const moveToRegisterNewUserPage = () => {
		navigate(`/register/${companyId.current}`);
	};

	useEffect(() => {
		if (adminMode || currentUserAdditionalData) {
			getAllCompanyUsers();
		}
	}, [adminMode, currentUserAdditionalData, getAllCompanyUsers]);

	return (
		<div className="company_users">
			<BlockHeading title={t('usersInCompanyTitle')} />
			<div className="company_users__in">
				{companyUsers && currentUserAdditionalData ? (
					<>
						<ul className="company_users__list">
							{companyUsers.map(({ role, email }, index) => {
								return (
									<li className="company_users__list__item" key={index}>
										<CompanyUsersBlock
											email={email}
											role={role}
											itsMyself={email === currentUserAdditionalData.email}
											getAllCompanyUsers={getAllCompanyUsers}
										/>
									</li>
								);
							})}
						</ul>
						<div className="company_users__block_link_w">
							<button
								className="company_users__block_link"
								type="button"
								onClick={moveToRegisterNewUserPage}
							>
								{t('registerNewUser')}
							</button>
						</div>
						<div>
							<button
								className="company_users__block_link"
								type="button"
								onClick={createInviteLinkToCompany}
							>
								<span>
									{t('copyInviteLinkFmt').replace(
										'{company}',
										currentUserAdditionalData.company.name
									)}
								</span>
								<span className="company_users__block_link_icon">
									<LinkIcon className="icon icon-link size_mod" />
								</span>
							</button>
						</div>
					</>
				) : loadingStatus === PROMISE_STATES.pending ? (
					<Spinner darkTheme />
				) : (
					<div className="company_users__error">{loadingErrorMessage}</div>
				)}
			</div>
		</div>
	);
};

export default CompanyUsers;
